/**
 * The hosted payment gateways the POS can charge through, as the frontends
 * know them.
 *
 * MIRROR of `pos-api/src/integrations/gateways/registry.ts` — pos-api does
 * not depend on this package, so the two tables are kept identical by hand.
 * The codes are `Inv_PaymentType.Code` values and must never change once a
 * tenant has a receipt on one: -3 Tap, -4 UPayments, -5 Tabby, -6 Tamara,
 * -7 MyFatoorah, -8 KNET Online. (-1 Unpaid, 0 Multi and -2 Loyalty are the POS's other
 * sentinels; every real tender is positive.)
 */

export type PaymentGatewayCode = 'tap' | 'upayments' | 'tabby' | 'tamara' | 'myfatoorah' | 'knet';

export interface PaymentGatewayInfo {
  code: PaymentGatewayCode;
  /** `Inv_PaymentType.Code` this gateway tenders under. */
  paymentTypeCode: number;
  nameEn: string;
  nameAr: string;
  /** The provider sends the customer an SMS code, so it needs a phone before opening. */
  requiresPhone: boolean;
  /** Buy-now-pay-later: not offered on returns / exchanges. */
  isBnpl: boolean;
}

export const PAYMENT_GATEWAYS: Record<PaymentGatewayCode, PaymentGatewayInfo> = {
  tap: { code: 'tap', paymentTypeCode: -3, nameEn: 'Tap', nameAr: 'تاب', requiresPhone: false, isBnpl: false },
  upayments: { code: 'upayments', paymentTypeCode: -4, nameEn: 'UPayments', nameAr: 'يو بايمنتس', requiresPhone: false, isBnpl: false },
  tabby: { code: 'tabby', paymentTypeCode: -5, nameEn: 'Tabby', nameAr: 'تابي', requiresPhone: true, isBnpl: true },
  tamara: { code: 'tamara', paymentTypeCode: -6, nameEn: 'Tamara', nameAr: 'تمارا', requiresPhone: true, isBnpl: true },
  myfatoorah: { code: 'myfatoorah', paymentTypeCode: -7, nameEn: 'MyFatoorah', nameAr: 'ماي فاتورة', requiresPhone: false, isBnpl: false },
  knet: { code: 'knet', paymentTypeCode: -8, nameEn: 'KNET Online', nameAr: 'كي نت أونلاين', requiresPhone: false, isBnpl: false },
};

export const PAYMENT_GATEWAY_CODES: readonly PaymentGatewayCode[] = ['tap', 'upayments', 'tabby', 'tamara', 'myfatoorah', 'knet'];

/** The gateway that tenders on an `Inv_PaymentType.Code`, or null for an ordinary method. */
export function paymentGatewayForCode(code: number): PaymentGatewayInfo | null {
  for (const c of PAYMENT_GATEWAY_CODES) {
    if (PAYMENT_GATEWAYS[c].paymentTypeCode === code) return PAYMENT_GATEWAYS[c];
  }
  return null;
}

export function isPaymentGatewayCode(code: number): boolean {
  return paymentGatewayForCode(code) != null;
}

/**
 * Which gateway a stored charge id belongs to. Tap's ids are its own
 * (`chg_…`); the other gateways prefix theirs with their code.
 */
export function paymentGatewayForChargeId(chargeId: string): PaymentGatewayCode {
  for (const c of PAYMENT_GATEWAY_CODES) {
    if (c !== 'tap' && chargeId.startsWith(`${c}_`)) return c;
  }
  return 'tap';
}
